<template>
  <b-dropdown
    class="flags-dropdown"
    :class="dropdownClasses"
    :variant="dropdownVariant"
    :right="right || inputMode"
    >
      <slot name="button-content" slot="button-content">
        <span class="truncate"
          v-b-tooltip.hover.html="tooltipContent"
        >{{ listLabel }}</span>
      </slot>

      <b-form-checkbox
        v-if="useOptionAll"
        class="flags-dropdown__options-item"
        v-model="all"
        @input="allChange">
        {{ allLabelOrDefault }}
      </b-form-checkbox>
      <b-form-checkbox
        v-for="(option, name) in options"
        :key="name"
        class="flags-dropdown__options-item"
        :checked="value[name]"
        @input="itemChange(name, $event)">
        {{ option.label }}
      </b-form-checkbox>
  </b-dropdown>
</template>
<style lang="sass" scoped>
  @import src/styles/defaults/variables
  @import "src/styles/defaults/mobile-first"

  .flags-dropdown
    ::v-deep
      > .dropdown-menu
        color: var(--font-color-primary)
    &__options-item
      margin-left: 10px
      margin-right: 10px
      white-space: nowrap
      display: block
      ::v-deep > label
        font-weight: normal
    &.input-mode
      padding: 0
      ::v-deep
        > .btn-link
          color: var(--font-color-primary)
        > .dropdown-menu
          background-color: var(--color-background-light)
        > .dropdown-toggle
          width: 100%
          text-align: start
          display: flex
          align-items: center
          justify-content: space-between
          &:hover,
          &:active,
          &:focus
            text-decoration: none
    &.fixed-width-right
      ::v-deep
        > .dropdown-toggle
          width: 100%
          text-align: start
          display: flex
          align-items: center
          justify-content: flex-end
    &.fixed-width
      ::v-deep
        > .dropdown-toggle
          width: 100%
          text-align: start
          display: flex
          align-items: center
          justify-content: flex-start
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
</style>

<script>
function tooltipItemLabel(label) {
  return `<span class="fa fa-check text-green"></span> ${label}`;
}

/**
 * Dropdown com suporte nativo a listas de items para ativar/desativar flags.
 *
 * Suporte ao modo de dropdown comum e a modo de input, simulando um input de dados.
 *
 * Controle de seleção com opção "Todos". Internamente, essa opção se responsabiliza por representar
 * o resultado da seleção manual de itens ou propagar seleção/remoção entre os itens.
 * IMPORTANTE: A funcionalidade "Todos" apenas se aplica aos itens configurados em "options".
 * Caso haja mais flags em values que não estejam configuradas como opção, elas serão ignoradas.
 *
 * Veja as documentações na sessão de "props" para ver todas as opções de configuração.
 *
 * @example
 * <s-flags-dropdown
 *   :options="options"
 *   v-model="value"
 *   [variant="link"]
 *   [input-mode]
 *   [text="Texto do botão"]
 *   [none-text="Nenhum"]
 *   [...]
 *  >
 *    [<span slot="button-content">Conteúdo do botão</span>]
 * </s-flags-dropdown>
 *
 * @event input:<flag_name> Evento disparado ao mudar um item específico. Observação, mudar a
 * seleção através da funcionalidade "Todos" irá disparar este evento caso esta flag tenha sofrido
 * alteração.
 * @param {Boolean} newValue Novo valor.
 */
export default {
  components: {
  },
  props: {
    /**
     * Objeto de flags no formato { valor1: boolean, valor2: boolean, ... }
     */
    value: Object,
    /**
     * Objeto de definição das opções no formato:
     * @example
     * {
     *   valor1: {
     *     label: 'string',
     *   },
     *   valor2: {
     *     label: 'Label do valor 2',
     *   },
     * }
     */
    options: Object,
    /**
     * Modo de input.
     * Gera estilização para que se pareça com um input de dados.
     */
    inputMode: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Ativa ou desativa a opção "Todos".
     */
    useOptionAll: {
      type: Boolean,
      default: true,
      required: false,
    },
    /**
     * Label para a opção "Todos".
     */
    allLabel: {
      type: String,
      required: false,
    },
    /**
     * Texto para o botão do dropdown quando há ausencia de qualquer seleção. Vísivel apenas caso o
     * slot button-content não seja definido.
     * Se booleano == true, usa o texto padrão "Nenhum".
     * Padrão: campo vazio.
     */
    noneText: {
      required: false,
      default: null,
    },
    /**
     * Opção nativa do b-dropdown. Ignorada quando inputMode ativo.
     */
    variant: {
      type: String,
      required: false,
    },
    /**
     * Opção nativa do b-dropdown. Quando inputMode ativo, então o padrão é igual a true.
     */
    right: {
      type: Boolean,
      required: false,
    },
    fixedWidth: {
      type: Boolean,
      required: false,
    },
    /**
     * Opção nativa do b-dropdown. Se definido, ignora textos automaticos gerados pela seleção de]
     * itens.
     */
    text: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      all: this.isAllSelected(),
      fromItemChange: false,
    };
  },
  methods: {
    isAllSelected() {
      return Object.keys(this.options).every(key => this.value[key]);
    },
    allChange(newValue) {
      if (this.fromItemChange) {
        this.fromItemChange = false;
        return;
      }

      let hasChangedItems = false;

      Object.keys(this.options).forEach((key) => {
        if (this.value[key] === newValue) {
          return;
        }
        hasChangedItems = true;
        this.$set(this.value, key, newValue);
        this.fireItemChange(key, newValue);
      });

      if (hasChangedItems) {
        this.$emit('input', this.value);
      }
    },
    itemChange(itemName, newValue) {
      if (this.value[itemName] === newValue) {
        // Ignorando alterações já aplicadas ao modelo base, exemplo, pelo "Todos".
        return;
      }

      this.$set(this.value, itemName, newValue);


      this.fromItemChange = this.refreshAll();

      this.fireItemChange(itemName, newValue);
      this.$emit('input', this.value);
    },
    fireItemChange(name, value) {
      this.$emit(`input:${name}`, value);
    },
    refreshAll() {
      const allSelected = this.isAllSelected();
      if (allSelected !== this.all) {
        this.all = allSelected;
        return true;
      }
      return false;
    },
  },
  computed: {
    allLabelOrDefault() {
      return this.allLabel || this.$t('flags-dropdown.all');
    },
    listLabel() {
      if (this.text) {
        return this.text;
      }

      if (this.all) {
        return this.allLabelOrDefault;
      }

      const selectedItems = Object.keys(this.options)
        .filter(k => this.value[k]);

      if (selectedItems.length > 0) {
        return selectedItems.map(k => this.options[k].label)
          .join(', ');
      }

      return this.noneText === true ? this.$t('flags-dropdown.none') : this.noneText;
    },
    dropdownVariant() {
      if (this.inputMode) {
        return 'link';
      }
      return this.variant;
    },
    dropdownClasses() {
      return {
        'form-control': this.inputMode,
        'input-mode': this.inputMode,
        'fixed-width': this.fixedWidth && !this.right,
        'fixed-width-right': this.fixedWidth && this.right,
      };
    },
    tooltipContent() {
      const tooltipConfig = {
        trigger: 'hover',
        placement: 'top',
        boundary: 'viewport',
      };
      const selectedItems = Object.keys(this.options)
        .filter(k => this.value[k]);

      if (selectedItems.length === 0) {
        // Retornando algúm conteúdo, pois nulo está causando problemas no tooltip.
        return {
          ...tooltipConfig,
          title: '',
        };
      }

      const title = selectedItems
        .map(value => tooltipItemLabel(this.options[value].label))
        .join('<br />');

      return {
        ...tooltipConfig,
        title,
        html: true,
      };
    },
  },
};
</script>
